import React, { useState, useRef, useEffect, useCallback } from 'react';
import { User, UserPlus, Search, ChevronDown } from 'lucide-react';

export function PlayerSearchInput({ value, onChange, onPlayerSelect, players, pageId, onCreatePlayer, onOpen, loading, placeholder = "Search player...", className = "" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState(value || '');
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Sync external value
  useEffect(() => { setSearch(value || ''); }, [value]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openDropdown = useCallback(() => {
    setIsOpen(true);
    if (onOpen) onOpen();
  }, [onOpen]);

  // Filter players: case-insensitive partial match, page-filtered
  // If pageId set: show players matching that page OR unassigned players (page_id null/undefined)
  // If pageId not set: show ALL players (no page filter)
  const filtered = players.filter(p => {
    if (pageId && p.page_id && p.page_id !== pageId) return false;
    if (!search) return true;
    return p.name?.toLowerCase().includes(search.toLowerCase());
  }).slice(0, 15);

  const exactMatch = players.some(p =>
    p.name?.toLowerCase() === search.toLowerCase() && (!pageId || !p.page_id || p.page_id === pageId)
  );
  const showCreate = search.trim().length > 0 && !exactMatch;

  const options = [...filtered];
  const totalOptions = options.length + (showCreate ? 1 : 0);

  const selectPlayer = useCallback((player) => {
    setSearch(player.name);
    setIsOpen(false);
    setHighlightIdx(-1);
    onChange(player.name);
    if (onPlayerSelect) onPlayerSelect(player);
  }, [onChange, onPlayerSelect]);

  const handleCreate = useCallback(async () => {
    if (!onCreatePlayer || !search.trim()) return;
    const newPlayer = await onCreatePlayer(search.trim());
    if (newPlayer) {
      setSearch(newPlayer.name);
      setIsOpen(false);
      setHighlightIdx(-1);
      onChange(newPlayer.name);
      if (onPlayerSelect) onPlayerSelect(newPlayer);
    }
  }, [onCreatePlayer, search, onChange, onPlayerSelect]);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') { openDropdown(); e.preventDefault(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(prev => Math.min(prev + 1, totalOptions - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIdx >= 0 && highlightIdx < options.length) {
        selectPlayer(options[highlightIdx]);
      } else if (highlightIdx === options.length && showCreate) {
        handleCreate();
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Scroll highlighted into view
  useEffect(() => {
    if (listRef.current && highlightIdx >= 0) {
      const items = listRef.current.children;
      if (items[highlightIdx]) items[highlightIdx].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            onChange(e.target.value);
            if (!isOpen) openDropdown(); else setIsOpen(true);
            setHighlightIdx(-1);
          }}
          onFocus={openDropdown}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full h-12 pl-10 pr-8 rounded-xl border-0 text-sm"
          style={{ background: 'rgba(0,0,0,0.3)', color: 'white' }}
          data-testid="player-search-input"
        />
        <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && (loading || totalOptions > 0) && (
        <div
          ref={listRef}
          className="absolute z-50 w-full mt-1 rounded-xl overflow-hidden shadow-2xl max-h-60 overflow-y-auto"
          style={{ background: '#12121f', border: '1px solid rgba(255,255,255,0.1)' }}
          data-testid="player-search-dropdown"
        >
          {loading ? (
            <div className="px-3 py-4 text-center text-sm text-gray-400" data-testid="player-search-loading">Loading players...</div>
          ) : (
            <>
              {options.map((p, idx) => (
            <button
              key={p.id}
              type="button"
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${highlightIdx === idx ? 'bg-white/10' : 'hover:bg-white/5'}`}
              onMouseEnter={() => setHighlightIdx(idx)}
              onClick={() => selectPlayer(p)}
              data-testid={`player-option-${p.id}`}
            >
              <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/5 shrink-0">
                <User className="w-3.5 h-3.5 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{p.name}</p>
                {p.player_tag && <p className="text-[10px] text-gray-500">{p.player_tag}</p>}
              </div>
            </button>
          ))}
          {showCreate && (
            <button
              type="button"
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors border-t border-white/5 ${highlightIdx === options.length ? 'bg-[#00F5FF]/10' : 'hover:bg-white/5'}`}
              onMouseEnter={() => setHighlightIdx(options.length)}
              onClick={handleCreate}
              data-testid="create-new-player-option"
            >
              <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(0,245,255,0.15)' }}>
                <UserPlus className="w-3.5 h-3.5 text-[#00F5FF]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#00F5FF]">Create "{search.trim()}"</p>
                <p className="text-[10px] text-gray-500">Add as new player</p>
              </div>
            </button>
          )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
